import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Button, Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import PageTitle from "../../layouts/PageTitle";
import SearchBox from "../../components/ui/SearchBox";
import Table from "../../components/ui/Table";
import Pagination from "../../components/ui/Pagination";
import UpdateOrderModel from "./UpdateOrderModal";
import ExportFieldsModal from "./ExportFieldsModal";
import OrderFilterModal from "./OrderFilterModal";
import OrdersTableSection from "./OrdersTableSection";

import { fetchOrders } from "../../../store/custom-actions/orders-actions/ordersSlicer";
import checkPermissions from "../../global-functions/checkPermissionsFunc";

export default function OrdersPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showUpdateModel, setShowUpdateModel] = useState(false);
  const [orderWhichUpdate, setOrderWhichUpdate] = useState({});
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [showExportFeilds, setShowExportFeilds] = useState(false);

  const { data: orders, loading } = useSelector((state) => state.ordersSlicer);
  const debounceRef = useRef();

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    refund_status: "",
    payment_status: "",
    payment_gateway: "",
    start_date: "",
    end_date: "",
    per_page: 12,
    page: 1,
  });

  const jobData = orders?.data || [];
  const totalPages = orders?.last_page || 1;
  const currentPage = orders?.current_page || 1;
  const totalItems = orders?.total || 0;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(fetchOrders(buildQuery()));
    }, 1000);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  const buildQuery = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "") params.append(key, value);
    });
    return `?${params.toString()}`;
  };

  const handlePagination = (page) => setFilters((prev) => ({ ...prev, page }));
  const handleSearch = (value) =>
    value && setFilters((prev) => ({ ...prev, search: value, page: 1 }));
  const handleFilterChange = (e) =>
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
      page: 1,
    }));

  const [arrayWhichSelected, setArrayWhichSelected] = useState([]);
  const handleCheckList = (ids) => {
    setArrayWhichSelected((prev) => {
      const idsArray = Array.isArray(ids) ? ids : [ids];
      return [...new Set([...prev, ...idsArray])];
    });
  };

  const handleUnCheckList = (ids) => {
    const idsArray = Array.isArray(ids) ? ids : [ids];
    setArrayWhichSelected((prev) =>
      prev.filter((item) => !idsArray.includes(item))
    );
  };

  return (
    <>
      <PageTitle motherMenu={"Dashboard"} activeMenu={"Orders"} />
      <div className="col-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <SearchBox
              searchValue={filters.search}
              setSearchValue={(v) =>
                setFilters((prev) => ({ ...prev, search: v }))
              }
              onSearch={handleSearch}
            />
            <div className="d-flex align-items-center">
              <Dropdown className="me-2">
                <Dropdown.Toggle variant="danger" id="dropdown-basic">
                  <span className="d-none d-sm-inline">
                    {t("Bulk Actions")}
                  </span>
                  <i className="fa-solid fa-circle-exclamation d-sm-none"></i>
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    disabled={orders?.length <= 0}
                    onClick={() => setShowExportFeilds(true)}
                  >
                    {t("Export All")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={arrayWhichSelected?.length <= 0}
                    onClick={() => setShowExportFeilds(true)}
                  >
                    {t("Export Selected")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Button
                variant="primary"
                onClick={() => setShowFilterOptions(true)}
              >
                {t("Filter Orders")}
              </Button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <div id="example_wrapper" className="dataTables_wrapper">
                <OrdersTableSection
                  data={jobData}
                  loading={loading.fetch}
                  currentPage={currentPage}
                  perPage={filters.per_page}
                  totalItems={totalItems}
                  onPageChange={handlePagination}
                  arrayWhichSelected={arrayWhichSelected}
                  handlePush={handleCheckList}
                  handleDelete={handleUnCheckList}
                  setShowUpdateModel={setShowUpdateModel}
                  setOrderWhichUpdate={setOrderWhichUpdate}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <OrderFilterModal
        show={showFilterOptions}
        onHide={() => setShowFilterOptions(false)}
        filters={filters}
        onChange={handleFilterChange}
      />

      <ExportFieldsModal
        show={showExportFeilds}
        arrayWhichSelected={arrayWhichSelected}
        setShowExportFeilds={setShowExportFeilds}
        loading={loading.fetch}
      />

      <UpdateOrderModel
        show={showUpdateModel}
        onHide={() => setShowUpdateModel(false)}
        order={orderWhichUpdate}
        submitLoading={loading.update}
        onUpdateSuccess={() => {
          // Refresh orders list after successful update
          dispatch(fetchOrders(buildQuery()));
        }}
      />
    </>
  );
}
